<script lang="ts">
    import { stopPropagation } from 'svelte/legacy';
    import { createEventDispatcher } from 'svelte';
    import type { RichMultiSelectDefinition } from 'header/types';
    import { uniq } from 'lodash-es';

    interface Props {
        field: RichMultiSelectDefinition;
    }

    let { field }: Props = $props();

    let searchInputValue = $state('');

    let listedOptions = $derived(
        field.options.filter(
            ({ key, label }) =>
                key.toLocaleLowerCase().includes(searchInputValue.toLowerCase()) ||
                label.toLocaleLowerCase().includes(searchInputValue.toLocaleLowerCase()),
        ),
    );

    let currentlySelected = $derived(field.value);

    const dispatch = createEventDispatcher();

    const dispatchChange = (value: Array<RichMultiSelectDefinition['key']>) => {
        //make sure that the values are correct if options changed
        const optionKeys = field.options.map(({ key }) => key);
        const correctValues = value.filter(key => optionKeys.includes(key));

        dispatch('change', {
            field: {
                ...field,
                value: correctValues,
            },
        });
    };

    const addAllListedToSelected = () => {
        if (field.changeable === false) return;

        const visibleOptionKeys = listedOptions.map(({ key }) => key);
        const newCurrentlySelected = uniq([...currentlySelected, ...visibleOptionKeys]);

        dispatchChange(newCurrentlySelected);
    };

    const removeAllListedFromSelected = () => {
        if (field.changeable === false) return;

        const visibleOptionKeys = listedOptions.map(({ key }) => key);
        const newCurrentlySelected = currentlySelected.filter(
            key => !visibleOptionKeys.includes(key),
        );

        dispatchChange(newCurrentlySelected);
    };
    // Right now we dispatch event on every change immediately, if performance
    // issue appears a good start would be to batch changes. Could debounce or
    // dispatch changes on list blur
    const toggleOption = (key: string) => {
        if (field.changeable === false) return;

        if (isItemSelected(key)) {
            return dispatchChange(currentlySelected.filter(val => val !== key));
        }

        dispatchChange([...currentlySelected, key]);
    };

    const isItemSelected = (key: string) => {
        return currentlySelected.includes(key);
    };
</script>

<div>
    {#if field.lazy === 'initialized'}
        <ul class="options">
            {#each listedOptions as { key, label } (key)}
                <li onclick={() => toggleOption(key)} class:active={isItemSelected(key)}>
                    <input
                        type="checkbox"
                        readonly
                        checked={isItemSelected(key)}
                        onclick={stopPropagation(() => toggleOption(key))}
                    />
                    {label}
                </li>
            {/each}
        </ul>
        <div>
            <input class="search-input" bind:value={searchInputValue} placeholder="search..." />
        </div>
        <button onclick={addAllListedToSelected} disabled={field.changeable === false}
            >Select all</button
        >
        <button onclick={removeAllListedFromSelected} disabled={field.changeable === false}
            >Deselect all</button
        >
    {:else}
        <p class="loading-placeholder">Loading...</p>
    {/if}
</div>

<style lang="scss" scoped>
    .options {
        width: 100%;
        height: 100px;
        overflow-x: hidden;
        overflow-y: scroll;
        list-style: none;
        font-size: 13px;
        padding-left: 0;
        margin-bottom: 0;
        border: 1px solid black;

        li {
            display: flex;
            align-items: center;
            cursor: default;

            &.active {
                background-color: #cecece;
            }
        }
    }
    .search-input {
        margin: 5px 0;
    }

    .loading-placeholder {
        font-size: 0.8rem;
        margin: 0;
    }
</style>
